import React, { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Loader2, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import apiService from '../services/api'
import PlatformBusinessRegistration from '../components/PlatformBusinessRegistration'

const ClaimProfileStart: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const emailFromQuery = useMemo(
    () => (searchParams.get('email') || '').trim().toLowerCase(),
    [searchParams],
  )

  const [email, setEmail] = useState(emailFromQuery)
  const [isLoading, setIsLoading] = useState(false)

  const inputClass =
    'w-full px-4 py-2.5 border border-gray-200 bg-white rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const normalizedEmail = email.trim().toLowerCase()
    if (!normalizedEmail) {
      toast.error('Please enter your email')
      return
    }
    setIsLoading(true)
    try {
      const checkResponse = await apiService.claimProfileCheck(normalizedEmail)
      const status = checkResponse?.data?.status as
        | 'CLAIMABLE'
        | 'REGISTER'
        | 'LOGIN'
        | 'VERIFY_EMAIL'
        | undefined

      if (status === 'CLAIMABLE') {
        await apiService.claimProfileSendOTP(normalizedEmail)
        toast.success('Verification code sent. Please check your email.')
        navigate('/claim-profile/verify', { state: { email: normalizedEmail }, replace: true })
        return
      }

      if (status === 'REGISTER') {
        toast('No account found for this email. Please register first.', { icon: '📝' })
        navigate('/register', { replace: true, state: { email: normalizedEmail } })
        return
      }

      if (status === 'VERIFY_EMAIL') {
        try {
          await apiService.resendVerificationEmail(normalizedEmail)
        } catch {
          // Even if resend fails, still route user to the verification screen.
        }
        toast('This account exists but is not verified yet. Enter the email code to continue.', { icon: '✉️' })
        navigate('/verify-email', { replace: true, state: { email: normalizedEmail } })
        return
      }

      toast('This account already exists. Please sign in.', { icon: '🔐' })
      navigate('/login', { replace: true, state: { email: normalizedEmail } })
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Could not continue claim profile flow. Please try again.'
      toast.error(String(message))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex bg-primary-50">
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-primary-200/70 via-primary-100 to-white text-gray-900 flex-col justify-between p-12 overflow-hidden border-r border-primary-200/50">
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-primary-300/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-primary-400/20 rounded-full blur-3xl" />

        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <img src="/logo.png" alt="BookMyYogaTTC" className="h-12 w-auto mb-16" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl font-bold leading-tight tracking-tight text-gray-900"
          >
            Claim your pre-created
            <br />
            <span className="text-primary-600">school admin profile.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-5 text-gray-600 text-base leading-relaxed max-w-md"
          >
            Enter your email to check eligibility. If your profile was created by our team, we will send a code so you can verify your email and set your password.
          </motion.p>

        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="relative z-10 bg-primary-200/40 border border-primary-300/50 rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-5 h-5 text-primary-600" />
            <p className="text-sm font-semibold text-gray-900">Next step</p>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            After verification and password setup, you can sign in normally and continue onboarding from your dashboard.
          </p>
        </motion.div>

        <div className="relative z-10 mt-8">
          <PlatformBusinessRegistration />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-12 bg-white">
        <div className="w-full max-w-[440px]">
          <div className="lg:hidden flex justify-center mb-8">
            <img src="/logo.png" alt="BookMyYogaTTC" className="h-10 w-auto" />
          </div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Claim profile</h2>
            <p className="mt-1.5 text-sm text-gray-500">Enter your email to begin account claim.</p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-8 space-y-5"
            onSubmit={handleSubmit}
          >
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="you@yourschool.com"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <p className="text-center text-sm text-gray-500 pt-2">
              Back to{' '}
              <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700 hover:underline">
                Sign in
              </Link>
            </p>

            <div className="lg:hidden pt-5 mt-2 border-t border-gray-100">
              <PlatformBusinessRegistration />
            </div>
          </motion.form>
        </div>
      </div>
    </div>
  )
}

export default ClaimProfileStart
